/**
 * Portal home — the referral link, the numbers that matter, and the most recent
 * activity. Everything deeper lives behind a sidebar section.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { classNames } from '~/utils/classNames';
import {
  CARD,
  Cell,
  CellGrid,
  COMMISSION_LABEL,
  COMMISSION_TONE,
  EmptyState,
  KIND_LABEL,
  Panel,
  PageHead,
  fmtDate,
  money,
  rate,
  usePortal,
} from './shared';

export default function Overview() {
  const { data } = usePortal();
  const { affiliate, funnel, recentCommissions, balance } = data;

  const clicks = funnel?.clicks ?? 0;
  const signups = funnel?.signups ?? 0;
  const paying = funnel?.payingCustomers ?? 0;
  const recent = (recentCommissions ?? []).slice(0, 5);

  return (
    <div className="flex flex-col gap-7">
      <PageHead
        title="Overview"
        subtitle={`You earn ${affiliate!.commissionRateBps / 100}% of everything a referred customer pays, for ${affiliate!.windowMonths} months from their signup.`}
      />

      <ReferralLinkCard code={affiliate!.code!} windowMonths={affiliate!.windowMonths} />

      <section>
        <h2 className="ui-caption mb-3 text-muted">Funnel</h2>
        <CellGrid columns={3}>
          <Cell label="Clicks" value={clicks} />
          <Cell label="Signups" value={signups} note={`${rate(signups, clicks)} of clicks`} />
          <Cell label="Paying customers" value={paying} note={`${rate(paying, signups)} of signups`} />
        </CellGrid>
      </section>

      <section>
        <h2 className="ui-caption mb-3 text-muted">Earnings</h2>
        <CellGrid columns={3}>
          <Cell label="Available" value={money(balance?.availableCents ?? 0)} accent note="Ready to withdraw" />
          <Cell label="Paid out" value={money(balance?.paidOutCents ?? 0)} />
          <Cell label="Lifetime" value={money(balance?.lifetimeCents ?? 0)} />
        </CellGrid>
      </section>

      <Panel
        title="Latest commissions"
        aside={
          <Link
            to="/affiliates/earnings"
            className="text-[11.5px] text-secondary hover:text-primary touch:inline-flex touch:min-h-10 touch:items-center"
          >
            View all →
          </Link>
        }
      >
        {recent.length > 0 ? (
          <div className="divide-y divide-hairline">
            {recent.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-4 px-4 py-3.5 sm:px-5">
                <div className="min-w-0">
                  <div className="text-[13.5px] font-medium text-primary">{money(c.commissionCents, c.currency)}</div>
                  <div className="mt-0.5 truncate text-[11.5px] text-muted">
                    {KIND_LABEL[c.paymentKind] ?? c.paymentKind} · on {money(c.amountCents, c.currency)} ·{' '}
                    {fmtDate(c.accruedAt)}
                  </div>
                </div>
                <span
                  className={classNames('ui-pill shrink-0', COMMISSION_TONE[c.status] ?? 'text-muted border-subtle')}
                >
                  {COMMISSION_LABEL[c.status] ?? c.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No commissions yet"
            body="They appear the moment a customer you referred pays for something. Start with the link above."
          />
        )}
      </Panel>
    </div>
  );
}

function ReferralLinkCard({ code, windowMonths }: { code: string; windowMonths: number }) {
  const [copied, setCopied] = useState(false);
  const referralLink = `${window.location.origin}/r/${code}`;

  function copy() {
    navigator.clipboard.writeText(referralLink).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => toast.error('Could not copy the link'),
    );
  }

  return (
    <div className={classNames(CARD, 'p-4 sm:p-6')}>
      <span className="ui-caption text-secondary">Your referral link</span>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <code className="flex-1 truncate rounded-lg border border-hairline bg-sunken px-3.5 py-2.5 font-geist-mono text-[13px] text-accent">
          {referralLink}
        </code>
        <button
          type="button"
          className={classNames(copied ? 'ui-btn-secondary' : 'ui-btn-accent', 'shrink-0')}
          onClick={copy}
        >
          <span className={copied ? 'i-ph:check text-[13px]' : 'i-ph:copy text-[13px]'} />
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>

      <p className="mt-3 text-[12px] leading-relaxed text-muted">
        Anyone who signs up after visiting this link is attributed to you, and you earn on everything they pay for the
        next {windowMonths} months.
      </p>
    </div>
  );
}
